import React, { useEffect, useState } from "react";
import { Alert, Button, Card, Descriptions, Form, Input, Select, Space, Steps, Typography } from "antd";
import { useNavigate } from "react-router-dom";
import { useFortiusClientService } from "../../services/fortius/FortiusClientServiceProvider";
import { useClientConfigService } from "../../services/clients/ClientConfigServiceProvider";
import { CreateClientRequest, CreatedClient } from "../../model/fortius/CreateClientRequest";
import { ClientSummary } from "../../model/fortius/ClientSummary";
import { ALL_COPY_GROUPS, buildCopyRows } from "../settings/sections/CopyFromClientModal";
import {
    EMPTY_IDENTITY,
    IdentitySection,
    IdentitySectionValue,
} from "../settings/sections/IdentitySection";

const { Title, Paragraph } = Typography;

// FORT-118 ships the wizard skeleton: an "Identity" step plus the review/submit
// step that actually calls the API. The intermediate section steps (branding,
// locale, etc.) reuse the same Panel components that drive the per-client
// Settings page; those land in follow-up tickets so this wizard renders a
// "Continue" through the placeholder steps for now.
//
// Test-coverage gap (intentional for this story):
//   * The Playwright spec exercises the wizard end-to-end with no defaults
//     set — it walks Identity → Defaults (passthrough) → Review → submit, so
//     the request body contains only { name, description }. The backend
//     applies its built-in defaults (BrandingSettings.Default, GBP / Europe-
//     London, a single "Standard" member category, no facility/POS types).
//   * The DbIntegration test on the backend posts an explicit-defaults
//     request (DefaultFacilityTypeNames, DefaultMemberCategories) to assert
//     the seed-from-bundle path.
//   * Nothing currently exercises "wizard collects defaults end-to-end"
//     because the section panels that would populate those fields land in
//     FORT-119+ (Identity), FORT-120 (subdomain), FORT-116/117 (Imagery),
//     FORT-111 (Feature flags), FORT-112/113/114 (Facilities/Schedules/
//     Pricing). The full wizard-with-defaults E2E lands with the last of
//     those tickets.
export const CreateClientWizard: React.FC = () => {
    const service = useFortiusClientService();
    const configService = useClientConfigService();
    const navigate = useNavigate();
    const [form] = Form.useForm<{ name: string; description?: string }>();

    const [currentStep, setCurrentStep] = useState(0);
    const [identity, setIdentity] = useState<{ name: string; description?: string } | null>(null);
    const [identitySection, setIdentitySection] = useState<IdentitySectionValue>(EMPTY_IDENTITY);
    const [submitting, setSubmitting] = useState(false);
    const [submitError, setSubmitError] = useState<string | null>(null);
    const [created, setCreated] = useState<CreatedClient | null>(null);
    // Optional "copy settings from an existing club" on creation. Seeds the new
    // tenant's forms/fees/banners/check-in config from the chosen source via the
    // generic settings PUT, right after the client row is created.
    const [existingClients, setExistingClients] = useState<ClientSummary[]>([]);
    const [copyFromClientId, setCopyFromClientId] = useState<number | undefined>(undefined);
    const [copyWarning, setCopyWarning] = useState<string | null>(null);

    useEffect(() => {
        let cancelled = false;
        service.listClients()
            // The API client resolves undefined on failure — keep the empty list.
            .then((clients) => { if (!cancelled) setExistingClients(Array.isArray(clients) ? clients : []); })
            .catch(() => { if (!cancelled) setExistingClients([]); });
        return () => { cancelled = true; };
    }, [service]);

    const steps = [
        { title: 'Identity' },
        { title: 'Defaults' },
        { title: 'Review' },
    ];

    const handleIdentitySubmit = async () => {
        const values = await form.validateFields();
        setIdentity({ name: values.name.trim(), description: values.description?.trim() });
        setCurrentStep(1);
    };

    const handleSubmit = async () => {
        if (!identity) return;
        const hasBranding = identitySection.clubName.trim() !== ''
            || identitySection.pageTitle.trim() !== ''
            || identitySection.tagline.trim() !== ''
            || identitySection.faviconUrl.trim() !== ''
            || identitySection.logoUrl.trim() !== '';
        const request: CreateClientRequest = {
            name: identity.name,
            description: identity.description ?? null,
            // Only attach a branding payload when the operator filled in at
            // least one field; otherwise the API applies BrandingSettings.Default.
            branding: hasBranding ? {
                ...{
                    primaryColor: '#000000',
                    sidebarBg: '',
                    submenuBg: '',
                    sidebarItemSelectedBg: '',
                    heroImageUrl: '',
                    backgroundImageUrl: '',
                    membershipCardBg: '#1E2878',
                },
                clubName: identitySection.clubName.trim(),
                pageTitle: identitySection.pageTitle.trim(),
                tagline: identitySection.tagline.trim(),
                faviconUrl: identitySection.faviconUrl.trim(),
                logoUrl: identitySection.logoUrl.trim(),
            } : null,
        };
        setSubmitting(true);
        setSubmitError(null);
        try {
            const result = await service.createClient(request);
            setCreated(result);
            // Seed config from the chosen source club. A copy failure must not
            // undo a successful creation — surface it as a non-blocking warning.
            if (copyFromClientId !== undefined) {
                try {
                    const source = await configService.getConfig(copyFromClientId);
                    if (source) {
                        await configService.updateSettings(result.client.id, {
                            settings: buildCopyRows(ALL_COPY_GROUPS, source.settings),
                        });
                    }
                } catch (copyErr) {
                    setCopyWarning(
                        copyErr instanceof Error
                            ? `Client created, but copying settings failed: ${copyErr.message}`
                            : 'Client created, but copying settings failed.',
                    );
                }
            }
        } catch (err: unknown) {
            setSubmitError(err instanceof Error ? err.message : 'Failed to create client.');
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Card data-testid="create-client-wizard">
            <Space direction="vertical" size="large" style={{ width: '100%' }}>
                <Title level={3} style={{ marginBottom: 0 }}>Create new client</Title>
                <Steps current={currentStep} items={steps} data-testid="create-client-wizard-steps" />

                {currentStep === 0 && (
                    <Form form={form} layout="vertical" data-testid="create-client-identity-form">
                        <Form.Item
                            label="Client name"
                            name="name"
                            rules={[{ required: true, message: 'Client name is required.' }]}
                        >
                            <Input data-testid="create-client-name-input" autoFocus />
                        </Form.Item>
                        <Form.Item label="Description" name="description">
                            <Input.TextArea data-testid="create-client-description-input" rows={3} />
                        </Form.Item>
                        <Button
                            type="primary"
                            data-testid="create-client-identity-next"
                            onClick={handleIdentitySubmit}
                        >
                            Continue
                        </Button>
                    </Form>
                )}

                {currentStep === 1 && (
                    <Space direction="vertical" data-testid="create-client-defaults-step" style={{ width: '100%' }}>
                        <Paragraph type="secondary">
                            Identity fields below populate the tenant's branding settings. Leave any field blank to
                            inherit the system default. Remaining sections (locale, facility/POS/category defaults)
                            will be set on per-section steps in follow-up tickets.
                        </Paragraph>
                        <IdentitySection
                            value={identitySection}
                            onChange={setIdentitySection}
                            // Subdomain slug can't be set during creation because the
                            // new tenant has no rows yet — operators do that from the
                            // Settings page once the tenant exists.
                            showSlugField={false}
                        />
                        <Form layout="vertical">
                            <Form.Item
                                label="Copy settings from an existing club (optional)"
                                help="Seeds the new club's forms, joining fee, banners and check-in message from the selected club."
                            >
                                <Select
                                    data-testid="create-client-copy-source"
                                    style={{ width: '100%', maxWidth: 480 }}
                                    placeholder="Don't copy — start with defaults"
                                    allowClear
                                    value={copyFromClientId}
                                    onChange={(v) => setCopyFromClientId(v)}
                                    options={existingClients.map((c) => ({ value: c.id, label: c.name }))}
                                />
                            </Form.Item>
                        </Form>
                        <Space>
                            <Button data-testid="create-client-defaults-back" onClick={() => setCurrentStep(0)}>
                                Back
                            </Button>
                            <Button
                                type="primary"
                                data-testid="create-client-defaults-next"
                                onClick={() => setCurrentStep(2)}
                            >
                                Continue
                            </Button>
                        </Space>
                    </Space>
                )}

                {currentStep === 2 && (
                    <Space direction="vertical" data-testid="create-client-review-step">
                        <Descriptions bordered column={1}>
                            <Descriptions.Item label="Name">{identity?.name}</Descriptions.Item>
                            <Descriptions.Item label="Description">{identity?.description ?? '—'}</Descriptions.Item>
                        </Descriptions>

                        {submitError && (
                            <Alert
                                type="error"
                                message={submitError}
                                data-testid="create-client-submit-error"
                            />
                        )}

                        {copyWarning && (
                            <Alert type="warning" message={copyWarning} data-testid="create-client-copy-warning" />
                        )}

                        {created && (
                            <Alert
                                type="success"
                                showIcon
                                data-testid="create-client-submit-success"
                                message={`Created tenant "${created.client.name}" (id ${created.client.id}).`}
                                description={
                                    created.s3BucketProvisioningWarning
                                        ? `S3 bucket warning: ${created.s3BucketProvisioningWarning}`
                                        : undefined
                                }
                                action={
                                    <Button
                                        size="small"
                                        data-testid="create-client-submit-success-back"
                                        onClick={() => navigate('/fortius/clients')}
                                    >
                                        Back to list
                                    </Button>
                                }
                            />
                        )}

                        <Space>
                            <Button data-testid="create-client-review-back" onClick={() => setCurrentStep(1)}>
                                Back
                            </Button>
                            <Button
                                type="primary"
                                loading={submitting}
                                disabled={!!created}
                                data-testid="create-client-submit-btn"
                                onClick={handleSubmit}
                            >
                                Create client
                            </Button>
                        </Space>
                    </Space>
                )}
            </Space>
        </Card>
    );
};
